import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ChevronDown, ChevronUp, Mail, MessageCircle, 
  HelpCircle, CreditCard, Zap, Shield, Users, ArrowLeft
} from 'lucide-react';

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-white/10 last:border-0">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between py-4 text-left hover:text-neon-mint transition-colors"
    >
      <span className="font-medium text-white pr-4">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-neon-mint flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="pb-4 text-gray-400 text-sm leading-relaxed">
        {answer}
      </div>
    )}
  </div>
);

const Support = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqCategories = [
    {
      title: 'Getting Started',
      icon: Users,
      faqs: [
        {
          question: 'How does TrafficFlow AI work?',
          answer: 'TrafficFlow AI is a credit-based traffic exchange platform. You earn credits by viewing other members\' promotions, and spend those credits to promote your own links. The more you engage, the more exposure you get!'
        },
        {
          question: 'How many credits do I start with?',
          answer: 'Free accounts start with 50 credits. When you upgrade to Pro, you receive 200 bonus credits plus 100 credits every month. Power plan users get 500 bonus credits plus 300 credits monthly.'
        },
        {
          question: 'How do I earn more credits?',
          answer: 'You can earn credits by: 1) Viewing other members\' promotions in the feed, 2) Claiming your daily login bonus, 3) Referring new members (you get 50 credits per referral), or 4) Purchasing credit packs.'
        }
      ]
    },
    {
      title: 'Credits & Billing',
      icon: CreditCard,
      faqs: [
        {
          question: 'What are the subscription plans?',
          answer: 'We offer three plans: Free ($0/month with 50 starting credits), Pro ($19/month with 200 bonus + 100 monthly credits), and Power ($39/month with 500 bonus + 300 monthly credits). Yearly plans save you 2 months!'
        },
        {
          question: 'How do I cancel my subscription?',
          answer: 'You can cancel your subscription at any time from your Billing page. Please note that cancellations must be made at least 3 days before your next renewal date to avoid being charged for the following period. After cancellation, you\'ll retain access to paid features until the end of your current billing period.'
        },
        {
          question: 'Do unused credits roll over?',
          answer: 'Yes! Your credits never expire and roll over month to month. Whether you earn them, receive them as monthly bonuses, or purchase them - they\'re yours to keep.'
        },
        {
          question: 'Can I get a refund?',
          answer: 'We offer refunds on a case-by-case basis within 7 days of purchase if you haven\'t used a significant portion of your credits or features. Please contact support@trafficflowai.com for refund requests.'
        }
      ]
    },
    {
      title: 'AI Features',
      icon: Zap,
      faqs: [
        {
          question: 'What AI features are included?',
          answer: 'Pro plans include AI Copy Generator (creates headlines, descriptions, CTAs) and AI Optimization Tips. Power plans additionally include AI Image Generator for creating promotional graphics and landing page images.'
        },
        {
          question: 'How does the AI content generator work?',
          answer: 'Simply enter a topic or describe your product/service, and our AI (powered by GPT-5.2) will generate compelling marketing copy including headlines, subheadlines, descriptions, and call-to-action text.'
        },
        {
          question: 'Is there a limit on AI generations?',
          answer: 'There\'s no strict limit on AI text generations for Pro and Power users. AI image generation (Power plan only) is subject to fair use to ensure quality service for all members.'
        }
      ]
    },
    {
      title: 'Landing Pages & Leads',
      icon: MessageCircle,
      faqs: [
        {
          question: 'How many landing pages can I create?',
          answer: 'Pro plans can create up to 5 landing pages, while Power plans can create up to 10. Each landing page comes with a unique URL and lead capture form.'
        },
        {
          question: 'How do I contact my captured leads?',
          answer: 'Pro and Power users can send messages to their leads directly through the platform. Go to the Leads page, select a lead, and use the messaging feature to reach out.'
        },
        {
          question: 'Can I export my leads?',
          answer: 'Currently, you can view and message leads within the platform. Export functionality is coming soon in a future update.'
        }
      ]
    },
    {
      title: 'Account & Security',
      icon: Shield,
      faqs: [
        {
          question: 'How do I upgrade or downgrade my plan?',
          answer: 'Visit the Billing page in your dashboard to change your plan. Upgrades take effect immediately with bonus credits. Downgrades take effect at the end of your current billing period.'
        },
        {
          question: 'Is my payment information secure?',
          answer: 'Yes! We use Stripe for all payment processing. Your credit card details are never stored on our servers - they\'re handled entirely by Stripe\'s PCI-compliant infrastructure.'
        },
        {
          question: 'How do I delete my account?',
          answer: 'To delete your account, please contact support@trafficflowai.com. We\'ll process your request within 48 hours. Note that account deletion is permanent and cannot be undone.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-void">
      {/* Header */}
      <header className="border-b border-white/10 bg-void-paper/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-neon-mint rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-black" />
            </div>
            <span className="font-heading font-bold text-xl text-white">TrafficFlow <span className="text-neon-mint">AI</span></span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-neon-mint/10 rounded-full mb-6">
            <HelpCircle className="w-8 h-8 text-neon-mint" />
          </div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
            How can we help?
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Find answers to common questions below, or reach out to our support team.
          </p>
        </div>

        {/* Contact Card */}
        <Card className="bg-gradient-to-r from-neon-mint/10 to-neon-violet/10 border-neon-mint/30 mb-12">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-neon-mint/20 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-neon-mint" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-white">Need more help?</h3>
                  <p className="text-gray-400 text-sm">Our support team typically responds within 24 hours</p>
                </div>
              </div>
              <a href="mailto:support@trafficflowai.com">
                <Button className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold">
                  <Mail className="w-4 h-4 mr-2" />
                  support@trafficflowai.com
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Sections */}
        <div className="space-y-8">
          {faqCategories.map((category, catIndex) => (
            <div key={catIndex}>
              <div className="flex items-center gap-3 mb-4">
                <category.icon className="w-5 h-5 text-neon-mint" />
                <h2 className="font-heading font-semibold text-xl text-white">{category.title}</h2>
              </div>
              <Card className="bg-void-paper border-white/10">
                <CardContent className="p-6">
                  {category.faqs.map((faq, faqIndex) => {
                    const globalIndex = `${catIndex}-${faqIndex}`;
                    return (
                      <FAQItem
                        key={faqIndex}
                        question={faq.question}
                        answer={faq.answer}
                        isOpen={openFAQ === globalIndex}
                        onClick={() => setOpenFAQ(openFAQ === globalIndex ? null : globalIndex)}
                      />
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 p-8 bg-void-paper rounded-xl border border-white/10">
          <h3 className="font-heading font-semibold text-xl text-white mb-2">Still have questions?</h3>
          <p className="text-gray-400 mb-4">We're here to help you succeed.</p>
          <a href="mailto:support@trafficflowai.com">
            <Button className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold">
              Contact Support
            </Button>
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-12 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          <Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link>
          <Link to="/support" className="hover:text-white transition-colors">Support</Link>
        </div>
      </footer>
    </div>
  );
};

export default Support;
